import { useCallback, useEffect, useState } from "react"

import { api, type AlcanceDeConfiguracoes, type RespostaDoCatalogo, type SistemaNoCatalogo } from "../api"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Skeleton } from "../componentes/Skeleton"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import { inteiro } from "../formato"
import { BOTAO_BRANCO, BOTAO_CANCELAR, CABECALHO_DO_SHEET, MAIUSCULA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

type AbaDosSistemas = "todos" | "ativos" | "inativos"

const ABAS_DOS_SISTEMAS: DescricaoDeAba<AbaDosSistemas>[] = [
  { chave: "todos", rotulo: "Todos", dica: "Mostra todos os sistemas do catálogo" },
  { chave: "ativos", rotulo: "Ativos", dica: "Mostra só os sistemas que aparecem na lista de escolha" },
  { chave: "inativos", rotulo: "Inativos", dica: "Mostra só os sistemas desativados" },
]

const POR_PAGINA = 10

type Gaveta = { sistema: SistemaNoCatalogo } | null

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase()

const siglaNaTela = (sigla: string) => sigla.replace(/_/g, " ")

const entidadesQueContinuam = (quantas: number) =>
  quantas === 0
    ? "Nenhuma entidade tem este sistema."
    : quantas === 1
      ? "A entidade que o tem continua com ele."
      : `As ${inteiro(quantas)} entidades que o têm continuam com ele.`

export function CatalogoDeSistemas({
  alcance,
  aoVerOndeEUsado,
}: {
  alcance: AlcanceDeConfiguracoes
  aoVerOndeEUsado?: (sigla: string) => void
}) {
  const [resposta, definirResposta] = useState<RespostaDoCatalogo | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDosSistemas>("todos")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [gaveta, definirGaveta] = useState<Gaveta>(null)
  const [desativando, definirDesativando] = useState<SistemaNoCatalogo | null>(null)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .catalogoCompleto()
      .then(definirResposta)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])

  useEffect(carregar, [carregar])

  if (resposta === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sistemas DATAFull</CardTitle>
        </CardHeader>
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const podeAlterar = resposta.pode_alterar
  const sistemas = resposta.dados
  const procurado = normalizar(busca)
  const filtrados = sistemas.filter(
    (s) =>
      (aba === "todos" || (aba === "ativos") === s.ativo) &&
      (procurado === "" || normalizar(s.sigla).includes(procurado) || normalizar(s.nome).includes(procurado)),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtrados.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtrados.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const mudar = (sistema: SistemaNoCatalogo, ativo: boolean) => {
    api
      .alterarSistema(sistema.id, { ativo })
      .then((r) => {
        definirResposta(r)
        toast.success(`${siglaNaTela(sistema.sigla)} ${ativo ? "reativado" : "desativado"}`)
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const vazio =
    sistemas.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Catalogo />
          </EmptyMedia>
          <EmptyTitle>Nenhum sistema no catálogo</EmptyTitle>
          <EmptyDescription>Os sistemas DATAFull ainda não foram cadastrados.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nada encontrado</EmptyTitle>
          <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nada nesta situação."}</EmptyDescription>
        </EmptyHeader>
        {busca !== "" && (
          <EmptyContent>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Sistemas DATAFull{" "}
          <ComDica
            dica={
              podeAlterar
                ? "O catálogo vale para todas as entidades. Se desativado, sai da lista de escolha e continua nas entidades que já o têm."
                : alcance.conta_de_plataforma
                  ? "O catálogo vale para todas as entidades, inclusive as reais, e esta é a conta de demonstração: ela o consulta, e só o Root o altera."
                  : "O catálogo vale para todas as entidades, e só o Root o altera. Os sistemas da sua entidade se marcam na ficha dela, em Entidades."
            }
          />
        </CardTitle>
      </CardHeader>

      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtrados.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          aoClicarNaLinha={podeAlterar ? (sistema) => definirGaveta({ sistema }) : undefined}
          abasNaBarra={
            <Abas
              nome="sistemas"
              rotulo="Situação dos sistemas"
              classe="mb-0"
              abas={ABAS_DOS_SISTEMAS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Sigla ou nome" />
          <Column<SistemaNoCatalogo>
            name="sigla"
            label="Sigla"
            enableSorting={false}
            render={(valor) => <strong>{siglaNaTela(String(valor))}</strong>}
          />
          <Column<SistemaNoCatalogo> name="nome" label="Nome" enableSorting={false} />
          <Column<SistemaNoCatalogo>
            name="descricao"
            label="Descrição"
            enableSorting={false}
            render={(valor) => <span className={cn("inline-block text-(--tinta-2)", MAIUSCULA)}>{valor === null ? "—" : String(valor)}</span>}
          />
          <Column<SistemaNoCatalogo>
            name="entidades_com_o_sistema"
            label="Entidades"
            enableSorting={false}
            render={(valor) => <span className="tabular-nums">{inteiro(Number(valor))}</span>}
          />
          <Column<SistemaNoCatalogo>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, sistema) => <Selo situacao={sistema.ativo ? "ativa" : "inativa"}>{sistema.ativo ? "Ativo" : "Inativo"}</Selo>}
          />
          {podeAlterar && (
            <Acao<SistemaNoCatalogo> label="Editar" icon={Icones.Anotacao} onClick={(sistema) => definirGaveta({ sistema })} />
          )}
          {podeAlterar && (
            <Acao<SistemaNoCatalogo>
              label="Ver onde é usado"
              icon={Icones.Buscar}
              visivel={(sistema) => sistema.entidades_com_o_sistema > 0}
              onClick={(sistema) => aoVerOndeEUsado?.(sistema.sigla)}
            />
          )}
          {podeAlterar && (
            <Acao<SistemaNoCatalogo> label="Desativar" icon={Icones.Dispensar} visivel={(sistema) => sistema.ativo} onClick={definirDesativando} />
          )}
          {podeAlterar && (
            <Acao<SistemaNoCatalogo>
              label="Reativar"
              icon={Icones.Reabrir}
              visivel={(sistema) => !sistema.ativo}
              onClick={(sistema) => mudar(sistema, true)}
            />
          )}
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar o sistema {desativando === null ? "" : siglaNaTela(desativando.sigla)}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Ele sai da lista de escolha. {entidadesQueContinuam(desativando?.entidades_com_o_sistema ?? 0)} É possível reativar
              depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, false)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && definirGaveta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {gaveta === null ? "" : `Sistema: ${siglaNaTela(gaveta.sistema.sigla)}`}
            </SheetTitle>
          </SheetHeader>
          {gaveta !== null && (
            <FormularioDoSistema
              key={gaveta.sistema.id}
              inicial={gaveta.sistema}
              aoCancelar={() => definirGaveta(null)}
              aoSalvar={(r, mensagem) => {
                definirResposta(r)
                definirGaveta(null)
                toast.success(mensagem)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

export function FormularioDoSistema({
  inicial,
  aoCancelar,
  aoSalvar,
}: {
  inicial: SistemaNoCatalogo
  aoCancelar: () => void
  aoSalvar: (resposta: RespostaDoCatalogo, mensagem: string) => void
}) {
  const [nome, definirNome] = useState(inicial.nome)
  const [descricao, definirDescricao] = useState(inicial.descricao ?? "")
  const [enviando, definirEnviando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const falta = faltaNoFormulario([["o nome", nome]])

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    api
      .alterarSistema(inicial.id, { nome, descricao: descricao.trim() === "" ? null : descricao })
      .then((r) => aoSalvar(r, `${siglaNaTela(inicial.sigla)} salvo`))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (falta === null && !enviando) salvar()
      }}
    >
      <div>
        <RotuloDoCampo htmlFor="sistema-nome" obrigatorio>
          Nome
        </RotuloDoCampo>
        <Input id="sistema-nome" required autoComplete="off" value={nome} onChange={(evento) => definirNome(evento.target.value)} />
      </div>
      <div>
        <RotuloDoCampo htmlFor="sistema-descricao">Descrição</RotuloDoCampo>
        <Input id="sistema-descricao" autoComplete="off" value={descricao} onChange={(evento) => definirDescricao(evento.target.value)} />
      </div>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={falta !== null || enviando} title={falta ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}
